import './_replies.scss';
import './_replies.dark-mode.scss';
import React, { useEffect, useState, useMemo } from 'react';
import { Row, Col, Grid, Button } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import arrayMove from 'array-move';

import Table from './components/Table';
import AddEditModal from './components/AddEditModal';
import Alert from '../../../components/Alert';
import Loader from '../../../components/Loaders/Loader';

import { getRepliesList, createReply, editReply, deleteReply, sortReplies } from '../../../services/endpoints/tickets';

const Replies = () => {
  const history = useHistory();

  const [initReplies, setFilteredReplies] = useState([]);
  const [error, setError] = useState('');
  const [modalError, setModalError] = useState('');
  const [pageLoading, setPageLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [isFetchingRequest, setIsFetchingRequest] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [modalData, setModalText] = useState({
    type: '',
    title: '',
    okBtnText: '',
  });
  const [values, setValues] = useState({ title: '', body: '' });
  const [searchList, setSearchList] = useState('');
  const toggleModal = () => {
    setOpenModal(!openModal);
    setModalError('');
  };
  const onSearchList = (e) => setSearchList(e.target.value);

  const onAddReply = () => {
    setValues({ title: '', body: '' });
    setModalText({
      type: 'add',
      title: 'Add reply',
      okBtnText: 'Add reply',
    });
    toggleModal();
  };

  const getData = async (pageLoader, listLoader) => {
    setError('');
    pageLoader && setPageLoading(true);
    listLoader && setListLoading(true);

    try {
      const response = await getRepliesList();

      if (response.success) {
        setFilteredReplies(response?.data);
      }
    } catch (error) {
      if (error?.data && error?.data?.error_message) {
        setError(error.data.error_message);
      } else {
        setError('Something went wrong');
      }
      setFilteredReplies([]);
    } finally {
      pageLoader && setPageLoading(false);
      listLoader && setListLoading(false);
    }
  };

  const onCreateReply = async () => {
    setModalError('');
    setIsFetchingRequest(true);

    try {
      const response = await createReply(values);

      if (response.success) {
        toggleModal();
        await getData(false, true);
      } else {
        setModalError('Something went wrong');
      }
    } catch (error) {
      if (error?.data && error?.data?.error_message) {
        setModalError(error.data.error_message);
      } else {
        setModalError('Something went wrong');
      }
    } finally {
      setIsFetchingRequest(false);
    }
  };

  const onEditReply = async () => {
    setModalError('');
    setIsFetchingRequest(true);

    try {
      const response = await editReply(values.id, { title: values.title, body: values.body });

      if (response.success) {
        toggleModal();
        await getData(false, true);
      } else {
        setModalError('Something went wrong');
      }
    } catch (error) {
      if (error?.data && error?.data?.error_message) {
        setModalError(error.data.error_message);
      } else {
        setModalError('Something went wrong');
      }
    } finally {
      setIsFetchingRequest(false);
    }
  };

  const onSubmitReply = () => {
    switch (modalData.type) {
    case 'add':
      onCreateReply();
      break;

    case 'edit':
      onEditReply();
      break;

    default:
      break;
    }
  };

  const onDeleteReply = async () => {
    setListLoading(true);

    try {
      const response = await deleteReply(values.id);

      if (response.success) {
        toggleModal();
        await getData(false, true);
      } else {
        setListLoading(false);
      }
    } catch (error) {
      setListLoading(false);
    }
  };

  const onConfirmDelete = () => {
    onDeleteReply();
    toggleModal();
  };

  const filteredReplies = useMemo(() => {
    if (!searchList) return initReplies;

    return initReplies.filter((reply) => (reply.body + reply.title).replace(/\s/g, '').toLowerCase().includes(searchList.replace(/\s/g, '').toLowerCase()));
  }, [searchList, initReplies]);

  const onSortReplies = async (items) => {
    setError('');
    setListLoading(true);

    try {
      const response = await sortReplies(items);

      if (response.success) {
        setFilteredReplies(response?.data);
      } else {
        setError('Something went wrong');
      }
    } catch (error) {
      if (error?.data && error?.data?.error_message) {
        setError(error.data.error_message);
      } else {
        setError('Something went wrong');
      }
    } finally {
      setListLoading(false);
    }
  };

  const onSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex !== newIndex) {
      const items = arrayMove(initReplies, oldIndex, newIndex);
      const itemsPosition = items.filter((item) => !item.default).map((item, index) => ({ id: Number(item.id), position: index + 1 }));

      onSortReplies(itemsPosition);
      setFilteredReplies(items);
    }
  };

  useEffect(() => {
    document.title = 'Replies';

    getData(true);
  }, []);

  return (
    <Grid fluid>
      <AddEditModal
        open={openModal}
        toggle={toggleModal}
        modalData={modalData}
        onSubmit={onSubmitReply}
        onConfirmDelete={onConfirmDelete}
        values={values}
        onChange={setValues}
        isFetching={isFetchingRequest}
        errorText={modalError}
      />

      <Row>
        {pageLoading ? (
          <Loader />
        ) : (
          <Col xs={12} lg={8} sm={9} className="details__col">
            <Button className="details__back-button" onClick={() => history.goBack()} bsStyle="link">
              <span>‹</span>
              Back
            </Button>
            {error && <Alert text={error} />}
            <Row>
              <Col xs={12}>
                <ul className="nav nav-tabs p-b">
                  <li>
                    <Button onClick={onAddReply}>Add reply</Button>
                  </li>
                  <li className="pull-right">
                    <div className="form-inline">
                      <label htmlFor="search-input-left" className="search-input-left__label" />
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search"
                        autoComplete="off"
                        id="search-input-left"
                        value={searchList}
                        onChange={onSearchList}
                        onKeyPress={(e) => {
                          if (e.which === 13 || e.keyCode === 13) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </div>
                  </li>
                </ul>

                <Table
                  items={filteredReplies}
                  toggleModal={toggleModal}
                  onSortEnd={onSortEnd}
                  setModalText={setModalText}
                  setValues={setValues}
                  hideHandle={searchList?.trim() !== ''}
                  loading={listLoading}
                />
              </Col>
            </Row>
          </Col>
        )}
      </Row>
    </Grid>
  );
};

export default Replies;
